<template>
	<custom-page v-if="customPage" :slug="customPage.slug"></custom-page>
	<div v-else class="welcome-page">
		<header class="welcome-heading">
			<div>
				<span class="client-eyebrow">{{ clientName }}</span>
				<h1>{{ $t('nav.home') }}</h1>
			</div>
			<div class="attention-summary" :aria-label="$t('nav.notifications')" aria-live="polite">
				<portal-icon name="bell" :size="19"></portal-icon>
				<strong>{{ actionCount }}</strong>
				<span>{{ $t('nav.notifications') }}</span>
			</div>
		</header>

		<section
			v-if="safeWelcomeGreeting"
			class="welcome-message"
			:class="{ 'has-background': backgroundImage }"
			:style="backgroundStyle"
		>
			<div class="welcome-message-content" v-html="safeWelcomeGreeting"></div>
		</section>

		<div v-if="loading" class="dashboard-loading" role="status" :aria-label="$t('nav.notifications')">
			<div class="loading-card" v-for="index in 4" :key="index">
				<span></span><span></span><span></span>
			</div>
		</div>

		<section v-else-if="loadError" class="dashboard-state" role="alert">
			<portal-icon name="alert-circle" :size="28"></portal-icon>
			<p>{{ $t('error.connecting-account') }}</p>
			<button type="button" :aria-label="$t('error.connecting-account')" @click="getActionItems">
				<portal-icon name="refresh" :size="20"></portal-icon>
			</button>
		</section>

		<section v-else-if="actionCount" class="action-dashboard" :aria-label="$t('nav.notifications')">
			<deliverables
				v-if="features.projectsEnabled"
				:deliverables="actionItems.assignedTasks || []"
				task-mode
				@refresh="getActionItems"
			></deliverables>
			<tickets
				v-if="features.ticketsEnabled"
				:tickets="actionItems.openTickets || []"
			></tickets>
			<deliverables
				v-if="features.projectsEnabled"
				:deliverables="actionItems.deliverables || []"
				@refresh="getActionItems"
			></deliverables>
			<invoices
				v-if="features.invoicesEnabled"
				:invoices="actionItems.invoices || []"
				@refresh="getActionItems"
			></invoices>
			<forms
				v-if="features.formsEnabled"
				:forms="actionItems.forms || []"
				@refresh="getActionItems"
			></forms>
			<proposals
				v-if="features.proposalsEnabled"
				:proposals="actionItems.proposals || []"
				:agreements="actionItems.agreements || []"
				@refresh="getActionItems"
			></proposals>
			<meetings
				v-if="features.meetingsEnabled"
				:meetings="actionItems.meetings || []"
				@refresh="getActionItems"
			></meetings>
			<subscription-requests
				v-if="features.invoicesEnabled"
				:subscription-requests="actionItems.subscriptionRequests || []"
				@refresh="getActionItems"
			></subscription-requests>
		</section>

		<section v-else-if="!paymentPlans.length" class="dashboard-state">
			<span class="empty-state-icon"><portal-icon name="check" :size="28"></portal-icon></span>
			<strong>0 {{ $t('nav.notifications') }}</strong>
		</section>

		<section v-if="!loading && !loadError && paymentPlans.length" class="upcoming-dashboard" :aria-label="$t('payment-plans.upcoming-payments')">
			<payment-plans :plans="paymentPlans"></payment-plans>
		</section>
	</div>
</template>

<script>
	import DOMPurify from 'dompurify';
	import DashboardService from '@/modules/welcome/DashboardService';
	import Deliverables from '@/modules/welcome/actionItems/Deliverables';
	import Invoices from '@/modules/welcome/actionItems/Invoices';
	import Forms from '@/modules/welcome/actionItems/Forms';
	import Meetings from '@/modules/welcome/actionItems/Meetings';
	import Proposals from '@/modules/welcome/actionItems/Proposals';
	import SubscriptionRequests from '@/modules/welcome/actionItems/SubscriptionRequests';
	import Tickets from '@/modules/welcome/actionItems/Tickets';
	import PaymentPlans from '@/modules/welcome/actionItems/PaymentPlans';
	import CustomPage from '@/modules/custom/CustomPage';

	export default {
		name: 'WelcomePage',

		components: {
			CustomPage,
			Tickets,
			SubscriptionRequests,
			Deliverables,
			Invoices,
			Meetings,
			Proposals,
			Forms,
			PaymentPlans,
		},

		data() {
			return {
				actionItems: null,
				dashboardService: new DashboardService(),
				loading: false,
				loadError: false,
			};
		},

		mounted() {
			this.getActionItems();
		},

		methods: {
			getActionItems() {
				this.loading = true;
				this.loadError = false;
				this.dashboardService
					.getActionItems()
					.then((response) => {
						this.actionItems = response.data;
					})
					.catch(() => {
						this.loadError = true;
					})
					.finally(() => {
						this.loading = false;
					});
			},
		},

		computed: {
			paymentPlans() {
				return (this.actionItems && this.actionItems.paymentPlans) || [];
			},
			customPage() {
				return this.$store.state.customPages.find((page) => page.isDefault);
			},

			domain() {
				return this.$store.state.domain || {};
			},

			branding() {
				return this.domain.customBranding || {};
			},

			features() {
				return this.$store.getters.getFeatures || {};
			},

			clientName() {
				return this.$store.getters.getClientName || '';
			},

			safeWelcomeGreeting() {
				return DOMPurify.sanitize(this.branding.welcomeGreeting || '');
			},

			backgroundImage() {
				return this.branding.loginSplash || '';
			},

			backgroundStyle() {
				if (!this.backgroundImage) return {};
				return {
					backgroundImage: `linear-gradient(110deg, rgba(16, 17, 15, 0.86), rgba(16, 17, 15, 0.5)), url("${this.backgroundImage}")`,
				};
			},

			actionCount() {
				if (!this.actionItems) return 0;
				return ['assignedTasks', 'openTickets', 'deliverables', 'invoices', 'proposals', 'meetings', 'subscriptionRequests', 'agreements', 'forms']
					.map((key) => (this.actionItems[key] || []).length)
					.reduce((total, count) => total + count, 0);
			},
		},
	};
</script>

<style scoped lang="scss">
	.welcome-page {
		width: 100%;
		max-width: 1240px;
		margin: 0 auto;
		padding: 34px clamp(18px, 4vw, 52px) 52px;
		color: var(--portal-text);
	}

	.welcome-heading,
	.attention-summary,
	.dashboard-state {
		display: flex;
		align-items: center;
	}

	.welcome-heading {
		justify-content: space-between;
		gap: 24px;
		margin-bottom: 24px;
	}

	.client-eyebrow {
		display: block;
		margin-bottom: 5px;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 600;
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}

	.welcome-heading h1 {
		margin: 0;
		color: var(--portal-text);
		font-size: clamp(28px, 4vw, 42px);
		font-weight: 500;
		letter-spacing: -0.035em;
		line-height: 1.1;
	}

	.attention-summary {
		gap: 7px;
		padding: 9px 12px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-button-radius, 99px);
		background: var(--portal-surface);
		color: var(--portal-muted);
		white-space: nowrap;
	}

	.attention-summary .portal-icon {
		color: var(--v-primary-base);
	}

	.attention-summary strong {
		color: var(--portal-text);
		font-weight: 600;
	}

	.welcome-message {
		position: relative;
		overflow: hidden;
		min-height: 150px;
		display: flex;
		align-items: center;
		margin-bottom: 28px;
		padding: clamp(24px, 5vw, 48px);
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 20px);
		background: var(--portal-surface);
		background-position: center;
		background-size: cover;
	}

	.welcome-message-content {
		position: relative;
		z-index: 1;
		max-width: 720px;
	}

	.welcome-message-content ::v-deep > :first-child {
		margin-top: 0;
	}

	.welcome-message-content ::v-deep > :last-child {
		margin-bottom: 0;
	}

	.welcome-message.has-background ::v-deep * {
		color: white !important;
	}

	.action-dashboard,
	.upcoming-dashboard,
	.dashboard-loading {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
		gap: 18px;
		align-items: start;
	}

	.upcoming-dashboard {
		margin-top: 18px;
	}

	.dashboard-state {
		min-height: 180px;
		justify-content: center;
		flex-direction: column;
		gap: 12px;
		padding: 28px;
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
		color: var(--portal-muted);
		text-align: center;
	}

	.dashboard-state p {
		margin: 0;
	}

	.dashboard-state button,
	.empty-state-icon {
		width: 44px;
		height: 44px;
		display: grid;
		place-items: center;
		border: 0;
		border-radius: var(--brand-button-radius, 50%);
		background: var(--v-blue_5-base);
		color: var(--v-primary-base);
	}

	.dashboard-state button {
		cursor: pointer;
	}

	.loading-card {
		min-height: 220px;
		display: grid;
		align-content: start;
		gap: 14px;
		padding: 18px;
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
	}

	.loading-card span {
		height: 18px;
		border-radius: 6px;
		background: var(--v-gray_20-base);
		animation: portal-pulse 1.4s ease-in-out infinite;
	}

	.loading-card span:first-child {
		width: 45%;
		height: 34px;
	}

	.loading-card span:last-child {
		width: 68%;
	}

	@keyframes portal-pulse {
		50% {
			opacity: 0.45;
		}
	}

	@media (max-width: 599px) {
		.welcome-page {
			padding: 24px 16px 32px;
		}

		.welcome-heading {
			align-items: flex-start;
			margin-bottom: 20px;
		}

		.attention-summary span {
			display: none;
		}

		.welcome-message {
			min-height: 130px;
			margin-bottom: 20px;
			padding: 24px;
			border-radius: var(--portal-radius-md);
		}

		.action-dashboard,
		.upcoming-dashboard,
		.dashboard-loading {
			grid-template-columns: 1fr;
			gap: 14px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.loading-card span {
			animation: none;
		}
	}
</style>
